import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "./ThemeContext";

const REMEMBER_KEY = "eva_remember_me";
const API_BASE = "https://api.logvi-havox.com";

function getApiBase(): string {
  return API_BASE;
}

export type SubitemMenu = {
  label: string;
  codigo: string;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  themeColor?: string | null;
  children?: SubitemMenu[];
};

export type ModuloDepartamento = {
  id?: number | string | null;
  label: string;
  codigo: string;
  themeColor?: string | null;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  subitems?: SubitemMenu[];
};

export type DepartamentoAcessivel = {
  id: number | string;
  nome: string;
  slug: string;
  routeKey?: string | null;
  themeColor?: string | null;
  iconKey?: string | null;
  modulos?: ModuloDepartamento[];
};

export type EnderecoDisponivel = {
  id: number | string;
  empresa_id?: number | string | null;
  nome?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
  ativo?: boolean;
  ordem?: number | null;
  label?: string | null;
  criado_em?: string | null;
};

export type EnderecoComModulos = EnderecoDisponivel & {
  departamentos_acessiveis?: DepartamentoAcessivel[];
  modulos_total?: number;
};

export type EmpresaSessao = {
  id?: number | string | null;
  razao_social?: string | null;
  cnpj?: string | null;
};

export type Sessao = {
  id: number;
  nome: string;
  email?: string;
  picture?: string | number | null;
  departamento?: string | null;
  funcao?: string | null;
  ativo?: boolean;
  empresa_razao_social?: string | null;
  empresa_cnpj?: string | null;
  empresa?: EmpresaSessao | null;
  empresa_id?: string | number | null;
  roles?: string[];
  solicitante_id?: string | number | null;
  conta_id?: string | number | null;
  uid?: string | number | null;
  scopes?: string[];
  ttl?: number | null;
  ts?: number | null;
  token_type?: string | null;
  device_id?: string | null;
  is_shared_device?: boolean;
  endereco_selecionado_id?: number | string | null;
  enderecos_disponiveis?: EnderecoDisponivel[];
  enderecos_com_modulos?: EnderecoComModulos[];
  departamentos_acessiveis?: DepartamentoAcessivel[];
};

type AuthContextValue = {
  loading: boolean;
  sessao: Sessao | null;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  apiBase: string;
};

type AuthMeResponse = {
  ok?: boolean;
  sessao?: any;
  empresa?: any;
  config?: any;
  departamentos_acessiveis?: any[];
  enderecos_disponiveis?: any[];
  enderecos_com_modulos?: any[];
  endereco_selecionado_id?: number | string | null;
} | null;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function toNullableString(value: any): string | null {
  if (value === undefined || value === null) return null;
  return String(value);
}

function toNullableNumber(value: any): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapSubitemRecursive(sub: any): SubitemMenu {
  return {
    label: String(sub?.label || ""),
    codigo: String(sub?.codigo || ""),
    devOnly: sub?.devOnly === true || sub?.dev_only === true,
    developed: sub?.developed === true,
    migrated: sub?.migrated === true,
    markOrange: sub?.markOrange === true || sub?.mark_orange === true,
    markBlue: sub?.markBlue === true || sub?.mark_blue === true,
    themeColor:
      sub?.themeColor !== undefined && sub?.themeColor !== null
        ? String(sub.themeColor)
        : sub?.theme_color !== undefined && sub?.theme_color !== null
        ? String(sub.theme_color)
        : null,
    children: Array.isArray(sub?.children)
      ? sub.children.map((child: any) => mapSubitemRecursive(child))
      : [],
  };
}

function mapModulo(mod: any): ModuloDepartamento {
  return {
    id: mod?.id !== undefined && mod?.id !== null ? mod.id : null,
    label: String(mod?.label || ""),
    codigo: String(mod?.codigo || ""),
    themeColor:
      mod?.themeColor !== undefined && mod?.themeColor !== null
        ? String(mod.themeColor)
        : mod?.theme_color !== undefined && mod?.theme_color !== null
        ? String(mod.theme_color)
        : null,
    devOnly: mod?.devOnly === true || mod?.dev_only === true,
    developed: mod?.developed === true,
    migrated: mod?.migrated === true,
    markOrange: mod?.markOrange === true || mod?.mark_orange === true,
    markBlue: mod?.markBlue === true || mod?.mark_blue === true,
    subitems: Array.isArray(mod?.subitems)
      ? mod.subitems.map((sub: any) => mapSubitemRecursive(sub))
      : [],
  };
}

function mapDepartamento(dep: any): DepartamentoAcessivel {
  return {
    id: dep?.id,
    nome: String(dep?.nome || ""),
    slug: String(dep?.slug || ""),
    routeKey:
      dep?.routeKey !== undefined && dep?.routeKey !== null
        ? String(dep.routeKey)
        : dep?.route_key !== undefined && dep?.route_key !== null
        ? String(dep.route_key)
        : null,
    themeColor:
      dep?.themeColor !== undefined && dep?.themeColor !== null
        ? String(dep.themeColor)
        : dep?.theme_color !== undefined && dep?.theme_color !== null
        ? String(dep.theme_color)
        : null,
    iconKey:
      dep?.iconKey !== undefined && dep?.iconKey !== null
        ? String(dep.iconKey)
        : dep?.icon_key !== undefined && dep?.icon_key !== null
        ? String(dep.icon_key)
        : null,
    modulos: Array.isArray(dep?.modulos)
      ? dep.modulos.map((mod: any) => mapModulo(mod))
      : [],
  };
}

function mapEndereco(end: any): EnderecoDisponivel {
  return {
    id: end?.id,
    empresa_id:
      end?.empresa_id !== undefined && end?.empresa_id !== null
        ? end.empresa_id
        : null,
    nome: toNullableString(end?.nome),
    logradouro: toNullableString(end?.logradouro),
    numero: toNullableString(end?.numero),
    complemento: toNullableString(end?.complemento),
    bairro: toNullableString(end?.bairro),
    cidade: toNullableString(end?.cidade),
    uf: toNullableString(end?.uf),
    cep: toNullableString(end?.cep),
    ativo:
      end?.ativo !== undefined && end?.ativo !== null
        ? end.ativo === true
        : undefined,
    ordem: toNullableNumber(end?.ordem),
    label: toNullableString(end?.label),
    criado_em: toNullableString(end?.criado_em),
  };
}

function mapEnderecoComModulos(end: any): EnderecoComModulos {
  return {
    ...mapEndereco(end),
    departamentos_acessiveis: Array.isArray(end?.departamentos_acessiveis)
      ? end.departamentos_acessiveis.map((dep: any) => mapDepartamento(dep))
      : [],
    modulos_total:
      end?.modulos_total !== undefined && end?.modulos_total !== null
        ? Number(end.modulos_total)
        : 0,
  };
}

function mapSessao(data: AuthMeResponse): Sessao | null {
  if (!data?.ok || !data?.sessao?.nome) {
    return null;
  }

  const raw = data.sessao;

  const roles: string[] = Array.isArray(raw.roles)
    ? raw.roles.map((x: any) => String(x))
    : [];

  const scopes: string[] = Array.isArray(raw.scopes)
    ? raw.scopes.map((x: any) => String(x))
    : [];

  const departamentosAcessiveis: DepartamentoAcessivel[] = Array.isArray(
    raw.departamentos_acessiveis
  )
    ? raw.departamentos_acessiveis.map((dep: any) => mapDepartamento(dep))
    : Array.isArray(data?.departamentos_acessiveis)
    ? data.departamentos_acessiveis.map((dep: any) => mapDepartamento(dep))
    : [];

  const enderecosDisponiveis: EnderecoDisponivel[] = Array.isArray(
    raw.enderecos_disponiveis
  )
    ? raw.enderecos_disponiveis.map((end: any) => mapEndereco(end))
    : Array.isArray(data?.enderecos_disponiveis)
    ? data.enderecos_disponiveis.map((end: any) => mapEndereco(end))
    : [];

  const enderecosComModulos: EnderecoComModulos[] = Array.isArray(
    raw.enderecos_com_modulos
  )
    ? raw.enderecos_com_modulos.map((end: any) => mapEnderecoComModulos(end))
    : Array.isArray(data?.enderecos_com_modulos)
    ? data.enderecos_com_modulos.map((end: any) => mapEnderecoComModulos(end))
    : [];

  const enderecoSelecionadoId =
    raw.endereco_selecionado_id !== undefined &&
    raw.endereco_selecionado_id !== null
      ? raw.endereco_selecionado_id
      : data?.endereco_selecionado_id !== undefined &&
        data?.endereco_selecionado_id !== null
      ? data.endereco_selecionado_id
      : null;

  return {
    id: Number(raw.id ?? raw.uid ?? 0),
    uid: raw.uid ?? null,
    nome: String(raw.nome || ""),
    email: raw.email ?? undefined,
    picture: raw.picture ?? null,
    departamento:
      typeof raw.departamento === "string" ? String(raw.departamento) : null,
    funcao: typeof raw.funcao === "string" ? String(raw.funcao) : null,
    ativo:
      raw.ativo !== undefined && raw.ativo !== null
        ? raw.ativo === true
        : undefined,
    empresa_razao_social: raw.empresa_razao_social ?? null,
    empresa_cnpj: raw.empresa_cnpj ?? null,
    empresa:
      data?.empresa && typeof data.empresa === "object"
        ? {
            id:
              data.empresa.id !== undefined && data.empresa.id !== null
                ? data.empresa.id
                : null,
            razao_social:
              data.empresa.razao_social !== undefined
                ? data.empresa.razao_social
                : raw.empresa_razao_social ?? null,
            cnpj:
              data.empresa.cnpj !== undefined
                ? data.empresa.cnpj
                : raw.empresa_cnpj ?? null,
          }
        : {
            id:
              raw.empresa_id !== undefined && raw.empresa_id !== null
                ? raw.empresa_id
                : null,
            razao_social: raw.empresa_razao_social ?? null,
            cnpj: raw.empresa_cnpj ?? null,
          },
    empresa_id:
      raw.empresa_id !== undefined && raw.empresa_id !== null
        ? raw.empresa_id
        : null,
    roles,
    scopes,
    solicitante_id:
      raw.solicitante_id !== undefined && raw.solicitante_id !== null
        ? raw.solicitante_id
        : null,
    conta_id:
      raw.conta_id !== undefined && raw.conta_id !== null ? raw.conta_id : null,
    ttl: raw.ttl !== undefined && raw.ttl !== null ? Number(raw.ttl) : null,
    ts: raw.ts !== undefined && raw.ts !== null ? Number(raw.ts) : null,
    token_type:
      raw.token_type !== undefined && raw.token_type !== null
        ? String(raw.token_type)
        : null,
    device_id:
      raw.device_id !== undefined && raw.device_id !== null
        ? String(raw.device_id)
        : null,
    is_shared_device: raw.is_shared_device === true,
    endereco_selecionado_id: enderecoSelecionadoId,
    enderecos_disponiveis: enderecosDisponiveis,
    enderecos_com_modulos: enderecosComModulos,
    departamentos_acessiveis: departamentosAcessiveis,
  };
}

async function fetchAuthMe(apiBase: string): Promise<AuthMeResponse> {
  const r = await fetch(`${apiBase}/auth/me`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
  });

  if (r.status === 401 || r.status === 403) {
    return null;
  }

  if (!r.ok) {
    throw new Error("Erro ao carregar sessão");
  }

  const data = await r.json().catch(() => null);
  return data;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setTheme, setSystemTitle } = useTheme();

  const apiBase = useMemo(() => getApiBase(), []);

  const rememberMe = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(REMEMBER_KEY) || "false") === true;
    } catch {
      return false;
    }
  }, []);

  const hasResolvedOnceRef = useRef(false);

  const clearRemember = useCallback(() => {
    if (!rememberMe) {
      try {
        localStorage.removeItem(REMEMBER_KEY);
      } catch {
        // ignora
      }
    }
  }, [rememberMe]);

  const applyConfigFromApi = useCallback(
    (config: any) => {
      if (!config) return;

      if (config.theme) {
        try {
          setTheme(config.theme);
        } catch {
          // ignora
        }
      }

      const stRaw = config.systemTitle;
      let st: string | null = null;

      if (Array.isArray(stRaw)) {
        st = stRaw.join(" • ");
      } else if (typeof stRaw === "string") {
        st = stRaw;
      }

      if (st) {
        setSystemTitle(st);
      }
    },
    [setTheme, setSystemTitle]
  );

  const { data, isLoading, error, refetch, isFetched } = useQuery<
    AuthMeResponse,
    Error
  >({
    queryKey: ["auth", "session", apiBase],
    queryFn: () => fetchAuthMe(apiBase),
    retry: false,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const sessao = useMemo(() => mapSessao(data ?? null), [data]);

  useEffect(() => {
    if (data?.config) {
      applyConfigFromApi(data.config);
    }
  }, [data, applyConfigFromApi]);

  useEffect(() => {
    if (isFetched) {
      hasResolvedOnceRef.current = true;
    }
  }, [isFetched]);

  useEffect(() => {
    if (isLoading) return;
    if (!hasResolvedOnceRef.current) return;

    const path = location.pathname;
    const isOnLogin = path === "/entrar" || path === "/login";
    const isOnSessaoEncerrada = path === "/sessao-encerrada";

    if (!sessao) {
      clearRemember();

      if (!isOnLogin && !isOnSessaoEncerrada) {
        navigate("/sessao-encerrada", { replace: true });
      }
    }
  }, [sessao, isLoading, clearRemember, location.pathname, navigate]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${apiBase}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }).catch(() => null);
    } finally {
      try {
        localStorage.removeItem(REMEMBER_KEY);
        localStorage.removeItem("access_token");
      } catch {
        // ignora
      }

      queryClient.setQueryData(["auth", "session", apiBase], null);
      queryClient.removeQueries({ queryKey: ["auth", "session", apiBase] });

      window.location.assign("/entrar");
    }
  }, [apiBase, queryClient]);

  const value: AuthContextValue = {
    loading: isLoading && !hasResolvedOnceRef.current,
    sessao,
    error: error?.message || null,
    refresh,
    logout,
    apiBase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  }
  return ctx;
}
